import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { RemixModalMetadata } from '@atlassian/editor-plugin-malleable-ui/malleableUiPluginType';

import type { AiSuggestionsPlugin, Suggestion } from '../../../aiSuggestionsPluginType';

type SuggestedRemixLaunchOptions = NonNullable<Suggestion['action']>['launchOptions'];

/**
 * The semantic remix request built for an accepted `SUGGESTED_REMIX` suggestion. These are the
 * "what to generate" bits the backend supplies; the remix engine fills in the runtime/environment
 * context (live content snapshot, space, product, etc.) itself.
 *
 * A `RemixModalMetadata` narrowed to the Suggested Remix source, so it can be handed straight to
 * `malleableUi.commands.startRemix`.
 */
export type SuggestedRemixMetadata = RemixModalMetadata & {
	remixSource: 'suggestedRemix';
};

/**
 * A fully-resolved suggested-remix request: the anchor `localIds` plus the metadata the remix
 * engine consumes. `triggerSuggestedRemix` returns this so the follow-up engine wiring (and tests)
 * can drive generation without re-deriving anything.
 */
export type SuggestedRemixRequest = {
	localIds?: string[];
	metadata: SuggestedRemixMetadata;
};

/**
 * Maps the backend-supplied `launchOptions` + prompt onto the metadata the remix engine consumes.
 * Pure and serialisable, so it can be unit-tested without standing up an editor.
 */
export function buildSuggestedRemixMetadata({
	launchOptions,
	userPrompt,
}: {
	launchOptions?: SuggestedRemixLaunchOptions;
	userPrompt?: string;
}): SuggestedRemixMetadata {
	return {
		userPrompt,
		dynamicUiType: launchOptions?.dynamicUiType as SuggestedRemixMetadata['dynamicUiType'],
		dynamicUiSubtype: launchOptions?.dynamicUiSubtype,
		aspectRatio: launchOptions?.aspectRatio,
		remixSource: 'suggestedRemix',
	};
}

/**
 * Resolves an accepted `SUGGESTED_REMIX` suggestion into a remix request and drives generation via
 * `malleableUi.commands.startRemix`. The backend supplies the semantic bits
 * (`launchOptions.dynamicUiType` / `dynamicUiSubtype` / `aspectRatio`, `userPrompt`, anchor
 * `localIds`); the remix engine owns generation, the in-editor thinking bar, insertion and cleanup.
 *
 * `malleableUi` is an optional dependency, so when it is absent (e.g. products that do not enrol the
 * remix plugin) this resolves the request without dispatching — keeping the plugin usable everywhere.
 * The resolved request is still returned so callers and tests can assert on it.
 *
 * Returns `undefined` when there is no editor API to drive.
 */
export function triggerSuggestedRemix({
	api,
	launchOptions,
	localIds,
	userPrompt,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	launchOptions?: SuggestedRemixLaunchOptions;
	localIds?: string[];
	userPrompt?: string;
}): SuggestedRemixRequest | undefined {
	if (!api?.core) {
		return undefined;
	}

	const request: SuggestedRemixRequest = {
		localIds,
		metadata: buildSuggestedRemixMetadata({ launchOptions, userPrompt }),
	};

	const startRemix = api.malleableUi?.commands?.startRemix;
	if (startRemix) {
		api.core.actions.execute(
			startRemix({ localIds: request.localIds, remixModalMetadata: request.metadata }),
		);
	}

	return request;
}
